import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutGrid,
  Users,
  Package,
  LogOut,
  X,
  ClipboardList,
  Menu,
  Repeat,
  ShoppingBag,
  History,
  Boxes,
  Truck,
  ChevronUp,
  Tag,
  BarChart3,
  CreditCard,
  PanelLeftClose,
  PanelLeftOpen,
  Zap,
} from "lucide-react";
import logoIcon from "../../../assets/logo.png";
import wholesumWordmark from "../../../assets/wholesum.png";
import warehouseDashboardHero from "../../../assets/warehouse-dashboard-hero.png";
import {
  clearAuthFlow,
  clearAuthSession,
  getAuthSession,
  getAuthUser,
  hasPermission,
} from "../../AdminAuth/components/authStore";
import DashboardSwitchModal from "../../Dashboard/components/DashboardSwitchModal";

const linkBase =
  "flex w-full items-center gap-3 rounded-[12px] px-3.5 py-2.5 text-[14px] font-semibold transition-all duration-200";

function WarehouseSidebarContent({
  onNavigate,
  onLogout,
  collapsed = false,
  onToggleCollapse = null,
  showCollapseControl = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const session = getAuthSession() || {};
  const user = getAuthUser() || {};

  const dashboardAccess = session?.dashboardAccess || {};
  const currentDashboard = session?.currentDashboard || "warehouse";
  const canSwitchDashboard = !!dashboardAccess?.canSwitchDashboard;

  const [switchModalOpen, setSwitchModalOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);

  const path = location.pathname;

  useEffect(() => {
    setActionsOpen(false);
  }, [path]);

  const isDashboard = path === "/admin/dashboard";
  const isEmployees = path.startsWith("/admin/dashboard/employees");
  const isProducts = path.startsWith("/admin/dashboard/products");
  const isInventory = path.startsWith("/admin/dashboard/inventory");
  const isStock = path.startsWith("/admin/dashboard/stock");
  const isInboundReceipts = path.startsWith("/admin/dashboard/inbound-receipts");
  const isCustomerOrders = path.startsWith("/admin/dashboard/customer-orders");
  const isWarehouseSales = path.startsWith("/admin/dashboard/warehouse-sales");
  const isPaymentCollections = path.startsWith("/admin/dashboard/payment-collections");
  const isActivityLogs = path.startsWith("/admin/dashboard/activity-logs");
  const isCoupons = path.startsWith("/admin/dashboard/coupons");

  const isSuperAdmin = String(user?.role || "").toLowerCase() === "super_admin";

  const canViewEmployees =
    isSuperAdmin ||
    hasPermission([
      "employees.view",
      "admin.employees.read",
      "warehouse.employees.read",
      "warehouse_employee.read",
      "wh.employees.read",
    ]);

  const canViewProducts =
    isSuperAdmin ||
    hasPermission([
      "products.view",
      "admin.products.read",
      "warehouse.products.read",
      "wh.products.read",
    ]);

  const canViewInventory =
    isSuperAdmin ||
    hasPermission([
      "inventory.view",
      "inventory.read",
      "admin.inventory.read",
      "warehouse.inventory.read",
      "warehouse.stock.read",
      "wh.inventory.read",
      "wh.orders.read",
      "wh.orders.create",
      "wh.inbound.read",
      "wh.inbound.accept",
    ]);

  const canViewActivityLogs = !!session;

  const canViewCustomerOrders =
    isSuperAdmin ||
    hasPermission([
      "wh.orders.read",
      "warehouse.orders.read",
      "orders.view",
      "orders.read",
    ]);

  const canViewCoupons =
    isSuperAdmin ||
    hasPermission([
      "wh.coupons.read",
      "wh.coupons.create",
      "wh.orders.create",
      "warehouse.panel.access",
    ]);

  const canViewPaymentCollections =
    isSuperAdmin || hasPermission(["wh.payments.read"]);

  const linkClass = (active) =>
    [
      linkBase,
      collapsed ? "justify-center px-0 [&>span]:hidden" : "",
      active
        ? "bg-[#fff2f4] text-[#cf5f6e] shadow-[inset_3px_0_0_#d86a78]"
        : "text-slate-500 hover:bg-slate-50 hover:text-[#d86a78]",
    ]
      .filter(Boolean)
      .join(" ");

  const go = (to) => {
    onNavigate?.();
    navigate(to);
  };

  const handleSwitched = () => {
    onNavigate?.();
    navigate("/admin/dashboard", { replace: true });
  };

  const navButton = (active, to, Icon, label) => (
    <button
      type="button"
      className={linkClass(active)}
      onClick={() => go(to)}
      title={collapsed ? label : undefined}
      aria-label={label}
    >
      <Icon size={18} />
      <span>{label}</span>
    </button>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className={collapsed ? "px-3 pt-3" : "px-4 pt-3"}>
        <div
          className={`flex h-[58px] items-center ${
            collapsed ? "justify-center" : "justify-between gap-3"
          }`}
        >
          <div
            className={`flex min-w-0 items-center ${
              collapsed ? "justify-center" : "gap-2.5"
            }`}
          >
            <img
              src={logoIcon}
              alt="WholeSum"
              className={`${
                collapsed ? "h-10 w-10" : "h-11 w-11"
              } shrink-0 select-none object-contain`}
              draggable={false}
            />
            {!collapsed ? (
              <img
                src={wholesumWordmark}
                alt="WholeSum"
                className="h-[30px] min-w-0 max-w-[118px] select-none object-contain"
                draggable={false}
              />
            ) : null}
          </div>

          {showCollapseControl && !collapsed ? (
            <button
              type="button"
              onClick={onToggleCollapse}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-[#f2c6cc] hover:bg-[#fff6f7] hover:text-[#d86a78]"
              aria-label="Collapse sidebar"
              title="Collapse sidebar"
            >
              <PanelLeftClose size={17} />
            </button>
          ) : null}
        </div>

        <div className="mt-2 h-px w-full bg-slate-200/80" />
      </div>

      {showCollapseControl && collapsed ? (
        <button
          type="button"
          onClick={onToggleCollapse}
          className="mx-auto mt-3 grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-[#f2c6cc] bg-[#fff6f7] text-[#d86a78] shadow-sm transition hover:bg-white"
          aria-label="Expand sidebar"
          title="Expand sidebar"
        >
          <PanelLeftOpen size={17} />
        </button>
      ) : null}

      <div className={`${collapsed ? "mt-3 px-2" : "mt-3 px-3"} min-h-0 flex-1 overflow-hidden`}>
        <nav className="sidebar-scroll flex h-full flex-col gap-1.5 overflow-y-auto pr-1">
          {navButton(isDashboard, "/admin/dashboard", LayoutGrid, "Dashboard")}

          {canViewEmployees
            ? navButton(
                isEmployees,
                "/admin/dashboard/employees",
                Users,
                "Employees"
              )
            : null}

          {canViewProducts
            ? navButton(
                isProducts,
                "/admin/dashboard/products",
                Package,
                "Products"
              )
            : null}

          {canViewInventory ? (
            <>
              {navButton(
                isInventory,
                "/admin/dashboard/inventory",
                ClipboardList,
                "Inventory"
              )}
              {navButton(isStock, "/admin/dashboard/stock", Boxes, "Stock")}
              {navButton(
                isInboundReceipts,
                "/admin/dashboard/inbound-receipts",
                Truck,
                "Inbound Receipts"
              )}
            </>
          ) : null}

          {canViewActivityLogs
            ? navButton(
                isActivityLogs,
                "/admin/dashboard/activity-logs",
                History,
                "Activity Logs"
              )
            : null}

          {canViewCoupons
            ? navButton(isCoupons, "/admin/dashboard/coupons", Tag, "Coupons")
            : null}

          {canViewCustomerOrders ? (
            <>
              {navButton(
                isCustomerOrders,
                "/admin/dashboard/customer-orders",
                ShoppingBag,
                "Customer Orders"
              )}
              {navButton(
                isWarehouseSales,
                "/admin/dashboard/warehouse-sales",
                BarChart3,
                "Sales Analytics"
              )}
              {canViewPaymentCollections
                ? navButton(
                    isPaymentCollections,
                    "/admin/dashboard/payment-collections",
                    CreditCard,
                    "Payment Collections"
                  )
                : null}
            </>
          ) : null}
        </nav>
      </div>

      <div
        className={`z-20 shrink-0 border-t border-rose-100/70 bg-white/95 shadow-[0_-10px_24px_rgba(15,23,42,0.035)] backdrop-blur-xl ${
          collapsed ? "px-2 pb-3 pt-2" : "px-3 pb-3 pt-2"
        }`}
      >
        <div className="relative">
          <div
            className={`absolute bottom-[calc(100%+8px)] left-0 right-0 transition-all duration-300 ease-out ${
              actionsOpen
                ? "pointer-events-auto translate-y-0 opacity-100"
                : "pointer-events-none translate-y-4 opacity-0"
            } ${collapsed ? "left-0 right-auto" : ""}`}
          >
            <div
              className={`${
                collapsed ? "w-[210px]" : "w-full"
              } space-y-2 rounded-[18px] border border-rose-100 bg-white/95 p-2.5 shadow-[0_22px_55px_rgba(15,23,42,0.16)] backdrop-blur-xl`}
            >
              {canSwitchDashboard ? (
                <button
                  type="button"
                  onClick={() => {
                    setActionsOpen(false);
                    setSwitchModalOpen(true);
                  }}
                  className="flex w-full items-center justify-center gap-3 rounded-[12px] bg-[#d86a78] px-5 py-3 text-[15px] font-semibold text-white transition hover:bg-[#c95b69]"
                >
                  <Repeat size={18} />
                  <span>Switch Panel</span>
                </button>
              ) : null}

              <button
                type="button"
                onClick={() => {
                  setActionsOpen(false);
                  onLogout?.();
                }}
                className="flex w-full items-center justify-center gap-3 rounded-[12px] border border-[#f2c6cc] bg-white px-5 py-3 text-[15px] font-semibold text-slate-400 transition hover:bg-slate-50 hover:text-[#d86a78]"
              >
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setActionsOpen((current) => !current)}
            className={`group/actions relative flex w-full overflow-hidden rounded-[14px] border text-[12px] font-semibold transition-all focus:outline-none focus:ring-4 focus:ring-rose-100 ${
              collapsed
                ? "h-11 items-center justify-center px-0"
                : "min-h-[118px] items-start px-3 py-3 text-left"
            } ${
              actionsOpen
                ? "border-[#e96578] bg-[linear-gradient(135deg,#fff0f3,#ffe4ea)] text-[#d94f66] shadow-[0_10px_24px_rgba(216,106,120,0.15)]"
                : "border-[#f2c6cc] bg-[linear-gradient(145deg,#fff5f7_0%,#ffe8ed_100%)] text-[#d86a78] hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(216,106,120,0.13)]"
            }`}
            aria-label={actionsOpen ? "Close sidebar actions" : "Open sidebar actions"}
            aria-expanded={actionsOpen}
          >
            {collapsed ? (
              <Zap size={18} />
            ) : (
              <>
                <span className="relative z-10 grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-white/80 text-[#ff4e70] shadow-sm">
                  <Zap size={17} fill="currentColor" />
                </span>
                <span className="relative z-10 ml-2 min-w-0 flex-1">
                  <span className="block text-[15px] font-black text-[#df4e68]">Actions</span>
                  <span className="mt-0.5 block text-[8px] font-medium text-slate-400">
                    Quick tools for warehouse operations
                  </span>
                </span>
                <ChevronUp
                  size={15}
                  className={`relative z-10 mt-1 shrink-0 transition-transform duration-300 ${
                    actionsOpen ? "rotate-180" : ""
                  }`}
                />
                <img
                  src={warehouseDashboardHero}
                  alt=""
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-[56px] w-[112px] max-w-none opacity-95 drop-shadow-[0_8px_12px_rgba(84,48,46,0.10)] transition-transform duration-500 group-hover/actions:scale-105"
                />
                <span className="absolute bottom-3 right-3 rotate-[-8deg] text-right text-[9px] font-semibold italic leading-tight text-[#e75970]">
                  Move Faster<br />Stock Smarter
                </span>
              </>
            )}
          </button>
        </div>

        <DashboardSwitchModal
          open={switchModalOpen}
          onClose={() => setSwitchModalOpen(false)}
          currentDashboard={currentDashboard}
          onSwitched={handleSwitched}
        />
      </div>
    </div>
  );
}

export default function WarehouseSidebar({ collapsed = false, onToggleCollapse }) {
  const navigate = useNavigate();

  const handleLogout = () => {
    clearAuthFlow();
    clearAuthSession();
    navigate("/admin/login", { replace: true });
  };

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-40 hidden border-r border-slate-200 bg-white transition-[width] duration-300 xl:block ${
        collapsed ? "w-[78px]" : "w-[232px]"
      }`}
    >
      <WarehouseSidebarContent
        onLogout={handleLogout}
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        showCollapseControl
      />
    </aside>
  );
}

export function WarehouseMobileSidebar({ open, onClose }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;

    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const handleLogout = () => {
    clearAuthFlow();
    clearAuthSession();
    onClose?.();
    navigate("/admin/login", { replace: true });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] xl:hidden">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/40"
        onClick={onClose}
        aria-label="Close menu overlay"
      />

      <aside className="absolute inset-y-0 left-0 flex w-[280px] max-w-[86vw] flex-col border-r border-slate-200 bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="inline-flex items-center gap-2 text-[13px] font-semibold text-slate-600">
            <Menu size={16} />
            Warehouse Menu
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-[10px] border border-slate-200 p-2 text-slate-500"
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
        </div>

        <WarehouseSidebarContent onNavigate={onClose} onLogout={handleLogout} />
      </aside>
    </div>
  );
}
